import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout, Header, Card } from '../components/mte/shell';
import { apiFetch, ApiError } from '../lib/auth-fetch';
import type { HeroId } from '../components/mte/placeholders';
import styles from './ChoixEnfant.module.css';

/**
 * Choix enfant - ecran 4/11 (D-NX-068, refactored 2026-09-17 spec Boussole).
 *
 * Page de selection du profil enfant dans l'espace famille.
 */

interface ChildProfile {
  childId: string;
  firstName: string;
  schoolLevel: 'CP' | 'CE1' | 'CE2' | 'CM1' | 'CM2';
  heroId: HeroId | null;
}

interface DiagnosticRow {
  diagnosticId: string;
  competenceId: string;
  scoreState: string;
  scoreValue: number | null;
  occurredAt: string;
}

type FetchState = 'idle' | 'loading' | 'ready' | 'error';

function ChoixEnfant(): JSX.Element {
  const [profils, setProfils] = useState<ChildProfile[]>([]);
  const [fetchState, setFetchState] = useState<FetchState>('idle');
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [hasAnyDiagnostic, setHasAnyDiagnostic] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFetchState('loading');
      try {
        const data = await apiFetch<{ children: ChildProfile[] }>('/api/parent/children');
        if (cancelled) return;
        const childrenList = data?.children ?? [];
        setProfils(childrenList);
        setFetchState('ready');
        setFetchError(null);

        (async () => {
          for (const child of childrenList) {
            try {
              const diag = await apiFetch<{ diagnostics: DiagnosticRow[] }>(
                '/api/device/children/' + encodeURIComponent(child.childId) + '/diagnostics',
              );
              if ((diag?.diagnostics ?? []).length > 0) {
                if (!cancelled) setHasAnyDiagnostic(true);
                break;
              }
            } catch {
              // Non-blocking probe.
            }
          }
        })();
      } catch (e) {
        if (cancelled) return;
        if (!(e instanceof ApiError)) console.warn('[choix-enfant] fetch failed', e);
        setProfils([]);
        setFetchState('error');
        setFetchError(
          e instanceof ApiError
            ? e.message
            : 'Impossible de charger les profils. Reessayer.'
        );
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleRetry = (): void => {
    setFetchState('idle');
    setFetchError(null);
    (async () => {
      setFetchState('loading');
      try {
        const data = await apiFetch<{ children: ChildProfile[] }>('/api/parent/children');
        setProfils(data?.children ?? []);
        setFetchState('ready');
      } catch (e) {
        setProfils([]);
        setFetchState('error');
        setFetchError(
          e instanceof ApiError
            ? e.message
            : 'Impossible de charger les profils. Reessayer.'
        );
      }
    })();
  };

  return (
    <Layout
      context="adulte"
      width="narrow"
      header={
        <Header
          context="adulte"
          logo={
            <>
              <span className={styles.brandMark} aria-hidden="true">✦</span>
              <span className={styles.brandText}>Mon Temps d'Étude</span>
            </>
          }
        />
      }
      footer={
        <footer className={styles.footer}>
          <nav className={styles.footerNav} aria-label="Navigation etape Choix enfant">
            <Link to="/entree/">← Retour à la connexion</Link>
            <span className={styles.footerStep} aria-current="step">Étape 2 / 11 — Choix du profil enfant</span>
          </nav>
          <p className={styles.footerMeta}>
            Vague 1 - D-NX-068 - ecran 4/11 - build 2026-09-17
          </p>
        </footer>
      }
    >
      <main className={styles.main} aria-labelledby="choix-enfant-title">
        <p className={styles.eyebrow}>Étape 2 — qui joue ?</p>
        <h1 id="choix-enfant-title" className={styles.title}>Choisis ton profil.</h1>
        <p className={styles.lede}>Selectionne un profil existant pour reprendre la mission.</p>

        {fetchState === 'loading' && (
          <section className={styles.loadingSection} aria-busy="true" aria-live="polite">
            <p className={styles.loadingMsg}>Chargement des profils...</p>
          </section>
        )}

        {fetchState === 'error' && (
          <section className={styles.errorSection} role="alert">
            <p className={styles.errorMsg}>{fetchError ?? 'Impossible de charger les profils.'}</p>
            <button type="button" className={styles.retryButton} onClick={handleRetry}>Reessayer</button>
          </section>
        )}

        {fetchState === 'ready' && profils.length === 0 && (
          <section className={styles.emptyState} aria-labelledby="empty-title">
            <span className={styles.emptyIllustration} aria-hidden="true">🦸</span>
            <h2 id="empty-title" className={styles.emptyTitle}>Cree le premier profil de ton enfant.</h2>
            <p className={styles.emptyLede}>
              Pour commencer l&apos;aventure, dis-nous son prenom et son annee scolaire.
              Vous pourrez choisir son heros ensemble juste apres.
            </p>
            <Link to="/onboarding-parent/" className={styles.primaryAction}>
              Creer le profil de ton premier enfant →
            </Link>
            <div className={styles.helpNoteActions}>
              <p className={styles.helpNote}>
                <Link to="/onboarding-parent/?mode=add" className={styles.addChildLink}>+ Ajouter un enfant</Link>
              </p>
              <p className={styles.helpNote}>
                <Link to="/famille/espace-parent/" className={styles.espaceParentLink}>Espace Parent (gerer profils)</Link>
              </p>
            </div>
          </section>
        )}

        {fetchState === 'ready' && profils.length > 0 && (
          <section className={styles.profilList} aria-label="Profils enfants enregistres">
            <h2 className={styles.profilListTitle}>Profils enregistres</h2>

            <div className={styles.helpNoteActions} aria-label="Gestion des profils">
              <Link to="/famille/espace-parent/" className={styles.secondaryAction} data-action="manage-profiles">
                Gérer les profils
              </Link>
              <Link to="/onboarding-parent/?mode=add" className={styles.secondaryAction} data-action="add-child">
                + Ajouter un enfant
              </Link>
            </div>

            <ul className={styles.profilGrid} role="list">
              {profils.map((profil) => {
                const hasHero = !!profil.heroId;
                return (
                  <li key={profil.childId} className={styles.profilItem}>
                    <Card elevation="md" childSized={true} className={styles.profilCard}>
                      <div className={styles.profilCardInner}>
                        <span className={styles.profilBadge} aria-hidden="true">🦸</span>
                        <span className={styles.profilName}>{profil.firstName}</span>
                        <span className={styles.profilLevel}>{profil.schoolLevel}</span>
                        {hasHero && (
                          <Link
                            to={`/onboarding/choix-heros/?childId=${encodeURIComponent(profil.childId)}&mode=rechoice`}
                            className={styles.kebabLink}
                            aria-label={`Changer de heros pour ${profil.firstName}`}
                            data-action="changer-de-heros"
                          >
                            Changer de heros
                          </Link>
                        )}
                      </div>
                      <div className={styles.profilActionRow}>
                        {hasHero ? (
                          <Link
                            to={`/enfant/aventure/?childId=${encodeURIComponent(profil.childId)}`}
                            className={styles.primaryAction}
                            data-action="continuer-mission"
                          >
                            Continuer la mission →
                          </Link>
                        ) : (
                          <Link
                            to={`/onboarding/choix-heros/?childId=${encodeURIComponent(profil.childId)}`}
                            className={styles.primaryAction}
                            data-action="choisir-heros"
                          >
                            Choisir un heros →
                          </Link>
                        )}
                        {hasAnyDiagnostic && (
                          <Link
                            to={`/enfant/diagnostic/?childId=${encodeURIComponent(profil.childId)}`}
                            className={styles.secondaryAction}
                            data-action="reprendre-diagnostic"
                          >
                            Reprendre mon diagnostic
                          </Link>
                        )}
                      </div>
                    </Card>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <section className={styles.helpNote} aria-label="A propos des profils">
          <p>
            <strong>Chaque enfant a son propre profil ?</strong> Les progressions sont strictement
            separees - pas de croisement entre enfants d&apos;une meme famille (multi-tenancy enforced).
          </p>
        </section>
      </main>
    </Layout>
  );
}

export default ChoixEnfant;
